import React, { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Calendar,
  ClipboardList,
  Mail,
  Phone,
  PlusCircle,
  RefreshCw,
  Trash2,
  Users,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import {
  ActivityType,
  ActivityTypeFilter,
  useActivities,
} from '../../hooks/useActivities';
import { ACTIVITY_META } from '../../components/activities/activityMeta';
import { LogActivityModal } from '../../components/activities/LogActivityModal';

type OutletCtx = {
  userData?: { uid?: string; companyId?: string; role?: string; displayName?: string };
};

const FILTER_OPTIONS: Array<{ key: ActivityTypeFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'call_outbound', label: 'Outbound' },
  { key: 'call_inbound', label: 'Inbound' },
  { key: 'meeting_in_person', label: 'Meet in-person' },
  { key: 'meeting_video', label: 'Meet video' },
  { key: 'email_sent', label: 'Email' },
  { key: 'sms_sent', label: 'SMS' },
  { key: 'note', label: 'Note' },
  { key: 'document_sent', label: 'Doc sent' },
];

const SINCE_OPTIONS: Array<{ days: number; label: string }> = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
  { days: 365, label: '12m' },
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------- Main page ----------

const ActivitiesPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';

  const {
    activities,
    loading,
    error,
    refresh,
    mineOnly,
    setMineOnly,
    activityTypeFilter,
    setActivityTypeFilter,
    sinceDaysAgo,
    setSinceDaysAgo,
    logActivity,
    deleteActivity,
  } = useActivities(companyId);

  const [logModalOpen, setLogModalOpen] = useState(false);

  const counts = useMemo(() => {
    const c: Record<string, number> = {
      total: activities.length,
      calls: 0,
      meetings: 0,
      messages: 0,
      followUpsDue: 0,
    };
    const now = Date.now();
    for (const a of activities) {
      if (a.activityType.startsWith('call_')) c.calls += 1;
      if (a.activityType.startsWith('meeting_')) c.meetings += 1;
      if (a.activityType === 'email_sent' || a.activityType === 'sms_sent')
        c.messages += 1;
      if (a.followUpAt && new Date(a.followUpAt).getTime() <= now) c.followUpsDue += 1;
    }
    return c;
  }, [activities]);

  const handleDelete = useCallback(
    async (activityId: string) => {
      if (!window.confirm('Delete this activity? This cannot be undone.')) return;
      await deleteActivity(activityId);
    },
    [deleteActivity]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ClipboardList}
        title="Client Activity"
        subtitle="Log calls, meetings, emails, and notes. Feeds your weekly funnel and engagement-gap alerts."
        badge="Beta"
        onRefresh={refresh}
        isRefreshing={loading}
        actions={
          <button
            onClick={() => setLogModalOpen(true)}
            className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm inline-flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            Log activity
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500">Total</div>
          <div className="mt-1 text-2xl font-bold text-white">{counts.total}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
            <Phone className="w-3 h-3" />
            Calls
          </div>
          <div className="mt-1 text-2xl font-bold text-white">{counts.calls}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
            <Users className="w-3 h-3" />
            Meetings
          </div>
          <div className="mt-1 text-2xl font-bold text-white">{counts.meetings}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
            <Mail className="w-3 h-3" />
            Messages
          </div>
          <div className="mt-1 text-2xl font-bold text-white">{counts.messages}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
          <div className="text-xs uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Due follow-ups
          </div>
          <div className="mt-1 text-2xl font-bold text-white">{counts.followUpsDue}</div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.map((f) => {
            const active = f.key === activityTypeFilter;
            return (
              <button
                key={f.key}
                onClick={() => setActivityTypeFilter(f.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-gray-900/40 border-gray-700 text-gray-300 hover:text-white hover:border-gray-500'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="flex gap-1">
          {SINCE_OPTIONS.map((s) => {
            const active = s.days === sinceDaysAgo;
            return (
              <button
                key={s.days}
                onClick={() => setSinceDaysAgo(s.days)}
                className={`px-2 py-1 rounded-md text-xs font-medium border ${
                  active
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-gray-900/40 border-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)}
            className="accent-blue-500"
          />
          Mine only
        </label>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading && activities.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Loading…
        </div>
      ) : activities.length === 0 ? (
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-10 text-center text-gray-400">
          <ClipboardList className="w-8 h-8 text-gray-500 mx-auto mb-2" />
          <p className="text-sm">No activities logged in the selected window.</p>
          <p className="text-xs text-gray-500 mt-2">
            Click <strong>Log activity</strong> to record your first touchpoint.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-3">When</th>
                <th className="text-left px-3 py-3">Type</th>
                <th className="text-left px-3 py-3">Client</th>
                <th className="text-left px-3 py-3">Outcome</th>
                <th className="text-left px-3 py-3">Notes</th>
                <th className="text-left px-3 py-3">Broker</th>
                <th className="text-right px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {activities.map((a) => {
                const meta =
                  ACTIVITY_META[(a.activityType as ActivityType) ?? 'other'] ??
                  ACTIVITY_META.other;
                const Icon = meta.icon;
                const isOwner = a.brokerId === userData?.uid;
                return (
                  <tr key={a.activityId} className="hover:bg-white/[0.02]">
                    <td className="px-3 py-3 text-gray-300 text-xs whitespace-nowrap">
                      {formatDate(a.occurredAt)}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs ${meta.color}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-white">
                      <div>{a.clientName || '—'}</div>
                      <div className="text-[0.7rem] text-gray-500">
                        {a.clientPhone || a.clientEmail || ''}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-300 text-xs">
                      {a.outcome ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-800 text-gray-200 font-mono">
                          {a.outcome}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-300 text-xs max-w-md truncate">
                      {a.notes || '—'}
                    </td>
                    <td className="px-3 py-3 text-gray-300 text-xs">{a.brokerName || '—'}</td>
                    <td className="px-3 py-3 text-right">
                      {isOwner && (
                        <button
                          onClick={() => handleDelete(a.activityId)}
                          className="text-gray-500 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LogActivityModal
        open={logModalOpen}
        onClose={() => setLogModalOpen(false)}
        onSubmit={async (args) => {
          await logActivity(args);
        }}
      />
    </div>
  );
};

export default ActivitiesPage;
